import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BiSearch, BiUserCheck, BiTrash } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";

// --- Added ---
// Define how many users to show per page
const ITEMS_PER_PAGE = 10;

const SuperAdminUserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      console.log("Fetched users:", response.data);
    } catch (err) {
      setError("Failed to fetch users. Please check permissions or network.");
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Reset to page 1 and clear selection whenever the search query changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage]);

  const handleToggleSelect = (userId) => {
    setSelectedIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (paginatedUsers.length > 0 && selectedIds.length === paginatedUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedUsers.map((u) => u.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected user(s)?`
      )
    )
      return;

    setIsDeleting(true);
    setError(null);
    setMessage("");
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API_BASE_URL}/users/bulk-delete`,
        { userIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(`Successfully deleted ${selectedIds.length} user(s).`);
      setSelectedIds([]);
      fetchUsers();
    } catch (err) {
      console.error("Bulk delete user error, trying fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) =>
            axios.delete(`${API_BASE_URL}/users/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          )
        );
        setMessage(`Successfully deleted ${selectedIds.length} user(s).`);
        setSelectedIds([]);
        fetchUsers();
      } catch (fallbackErr) {
        setError(err.response?.data?.message || "Failed to delete selected users.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`))
      return;
    setMessage("");
    setError(null);
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_BASE_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`User "${userName}" was deleted.`);
      setSelectedIds((prev) => prev.filter((id) => id !== userId));
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user.");
      console.error("Delete user error:", err);
    }
  };

  const handleImpersonate = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to log in as ${userName}?`))
      return;
    setMessage("");
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/super-admin/impersonate/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      localStorage.setItem("originalToken", token);
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      window.location.href = `/dashboard/${response.data.user.roleName.replace(
        "_",
        "-"
      )}`;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to impersonate user.");
      console.error("Impersonation error:", err);
    }
  };


  // --- Changed ---
  // This filtering logic remains the same
  const filteredUsers = users.filter(
    (user) =>
      `${user.first_name} ${user.last_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // --- Added ---
  // Calculate pagination variables based on the *filtered* list
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );
  // --- End Added ---

  if (loading)
    return (
      <div className="text-center text-gray-500 p-6">Loading users...</div>
    );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header and Search */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
          All System Users
        </h1>
        <div className="relative w-full sm:w-64">
          <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 text-center text-red-600 bg-red-100 p-3 rounded">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-4 text-center text-green-600 bg-green-100 p-3 rounded">
          {message}
        </div>
      )}

      {/* Bulk actions banner */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>{selectedIds.length} of {paginatedUsers.length} selected on this page</span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition disabled:opacity-50"
          >
            <BiTrash className="mr-1 h-4 w-4" />
            {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </button>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={paginatedUsers.length > 0 && selectedIds.length === paginatedUsers.length}
                    onChange={handleSelectAll}
                    aria-label="Select all users on this page"
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Organization
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-gray-50 transition-colors duration-150 ${
                      selectedIds.includes(user.id) ? "bg-blue-50/50" : ""
                    }`}
                  >
                    <td className="px-4 py-3 text-sm w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(user.id)}
                        onChange={() => handleToggleSelect(user.id)}
                        aria-label={`Select user ${user.first_name} ${user.last_name}`}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      <Link
                        to={`/super-admin/users/${user.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {user.first_name} {user.last_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {user.role_name.replace("_", " ")}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.organization_name || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium space-x-3">
                      <Link
                        to={`/super-admin/users/${user.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View
                      </Link>
                      <Link
                        to={`/super-admin/users/${user.id}/edit`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() =>
                          handleImpersonate(
                            user.id,
                            `${user.first_name} ${user.last_name}`
                          )
                        }
                        className="text-purple-600 hover:text-purple-800"
                        title="Impersonate User"
                      >
                        <BiUserCheck className="inline h-5 w-5" />
                      </button>
                      <button
                        onClick={() =>
                          handleDelete(
                            user.id,
                            `${user.first_name} ${user.last_name}`
                          )
                        }
                        className="text-red-600 hover:text-red-800"
                        title="Delete User"
                      >
                        <BiTrash className="inline h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-4 mt-4">
        {filteredUsers.length > 0 ? (
          paginatedUsers.map((user) => (
            <div
              key={user.id}
              className={`border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                selectedIds.includes(user.id) ? "ring-2 ring-blue-500 bg-blue-50/30" : ""
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(user.id)}
                    onChange={() => handleToggleSelect(user.id)}
                    aria-label={`Select user ${user.first_name} ${user.last_name}`}
                    className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                  <h2 className="text-lg font-semibold text-gray-800">
                    {user.first_name} {user.last_name}
                  </h2>
                </div>
                <button
                  onClick={() =>
                    handleImpersonate(
                      user.id,
                      `${user.first_name} ${user.last_name}`
                    )
                  }
                  className="text-purple-600 hover:text-purple-800"
                  title="Impersonate User"
                >
                  <BiUserCheck className="inline h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {user.email}
              </p>
              <p className="text-sm text-gray-600 capitalize">
                <span className="font-medium">Role:</span>{" "}
                {user.role_name.replace("_", " ")}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Organization:</span>{" "}
                {user.organization_name || "N/A"}
              </p>
              <div className="mt-3 flex justify-end items-center space-x-3 text-sm">
                <Link
                  to={`/super-admin/users/${user.id}`}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  View
                </Link>
                <Link
                  to={`/super-admin/users/${user.id}/edit`}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Edit
                </Link>
                <button
                  onClick={() =>
                    handleDelete(
                      user.id,
                      `${user.first_name} ${user.last_name}`
                    )
                  }
                  className="text-red-600 hover:text-red-800"
                  title="Delete User"
                >
                  <BiTrash className="inline h-4 w-4" />
                </button>
              </div>
            </div>
          ))

        ) : (
          <p className="text-center text-sm text-gray-500 py-4">
            No users found.
          </p>
        )}
      </div>

      {/* --- Added --- */}
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
      {/* --- End Added --- */}
    </div>
  );
};

export default SuperAdminUserList;